<script setup lang="ts" generic="T extends Option">
	import {
		Combobox,
		ComboboxButton,
		ComboboxInput,
		ComboboxLabel,
		ComboboxOption,
		ComboboxOptions,
		TransitionRoot,
	} from '@headlessui/vue'
	import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
	import { useDebounceFn } from '@vueuse/core'
	import Fuse from 'fuse.js'
	import { isArray } from 'lodash'
	import { Ref, computed, ref, toRaw, watch } from 'vue'

	import LoadingSpin from '@/components/icons/LoadingSpin.vue'

	import ShowPage from '@/modules/confirmations/pages/ShowPage.vue'

	export interface Option {
		id: number | string | undefined
	}
	interface FuseOptions {
		includeScore?: boolean
		threshold?: number
		findAllMatches?: boolean
		shouldSort?: boolean
	}

	interface Props {
		options: T[]
		modelValue: T | any
		limit?: number
		enableFuse?: boolean
		fuseOptions?: FuseOptions
		fuseKeys?: string[]
		loadingOptions?: boolean
		label?: string
		placeholder?: string
		defaultValue?: T
		multiple?: boolean
		isRequired?: boolean
		disabled?: boolean
		disabledClass?: string
		debounceDelay?: number
		resetQueryAfterSelect?: boolean
		hideOptionsAfterSelect?: boolean
		showInput?: boolean
		showButton?: boolean
		inputTooltip?: string
		error?: string | Ref<string>
		showOptionsByButton?: boolean
		inputOverlapTooltip?: string
		displayValue?: (value: T) => string
		onModelChange?: (value: T | undefined) => void
		afterModelChange?: (value: T | undefined) => void
		onInputChange?: (value: string) => void
		onFocus?: () => void
		onBlur?: () => void
		beforeEnter?: () => void
		afterEnter?: () => void
		beforeLeave?: () => void
		afterLeave?: () => void
		preventAutoFocus?: boolean
		preventScrollOnFocus?: boolean
	}
	interface Emits {
		(event: 'update:modelValue', value: any): boolean
	}

	const props = withDefaults(defineProps<Props>(), {
		options: Array<any>,
		label: '',
		placeholder: '',
		enableFuse: false,
		resetQueryAfterSelect: true,
		hideOptionsAfterSelect: false,
		showInput: true,
		showButton: true,
		multiple: false,
		isRequired: false,
		onModelChange: undefined,
		showOptionsByButton: false,
		fuseKeys: () => [],
		displayValue: () => '',
		onInputChange: () => null,
		onFocus: () => null,
		onBlur: () => null,
		afterLeave: () => null,
		afterEnter: () => null,
		beforeLeave: () => null,
		beforeEnter: () => null,
		preventAutoFocus: false,
		preventScrollOnFocus: false,
	})

	const emit = defineEmits<Emits>()
	const value = ref<T>()
	const query = ref<string>('')
	const inputElement = ref<any>({ el: null })
	const compoboxElement = ref<any>({ el: null })
	const showOptions = ref<boolean>(false)

	// Dirección del panel de opciones: hacia abajo por defecto, hacia arriba
	// cuando no hay espacio suficiente (evita que quede pegado al borde de la
	// página o de un modal con overflow).
	const wrapperElement = ref<HTMLElement | null>(null)
	const dropUp = ref<boolean>(false)
	const panelPosition = ref<{ top: number; left: number; width: number }>({
		top: 0,
		left: 0,
		width: 0,
	})
	// max-h-44 = 11rem = 176px + margen de seguridad
	const PANEL_MAX_HEIGHT = 184

	const getScrollableParent = (el: HTMLElement | null): HTMLElement | null => {
		let parent = el?.parentElement ?? null
		while (parent) {
			const overflowY = getComputedStyle(parent).overflowY
			if (overflowY === 'auto' || overflowY === 'scroll') return parent
			parent = parent.parentElement
		}
		return null
	}

	const updateDropDirection = () => {
		const el = wrapperElement.value
		if (!el) return
		const rect = el.getBoundingClientRect()
		const scrollParent = getScrollableParent(el)
		const boundaryBottom =
			scrollParent ?
				scrollParent.getBoundingClientRect().bottom
			:	window.innerHeight
		const spaceBelow = boundaryBottom - rect.bottom
		const spaceAbove = rect.top
		// Flip up cuando falta espacio abajo, solo si hay algo de espacio arriba
		// (arriba >= 64px para que no quede fuera de pantalla).
		// PANEL_MAX_HEIGHT es una estimación; con pocas opciones hay hueco cosmético.
		dropUp.value = spaceBelow < PANEL_MAX_HEIGHT && spaceAbove >= 64

		// Calcular posición del panel con Teleport.
		// Si flipea hacia arriba, posiciona encima del input.
		// Si abre hacia abajo, posiciona debajo del input.
		const gap = 4 // Pequeño espaciado entre input y panel
		if (dropUp.value) {
			panelPosition.value = {
				top: rect.top - PANEL_MAX_HEIGHT - gap,
				left: rect.left,
				width: rect.width,
			}
		} else {
			panelPosition.value = {
				top: rect.bottom + gap,
				left: rect.left,
				width: rect.width,
			}
		}
	}

	const filteredOptions = computed(() => {
		if (!props.enableFuse) return props.options

		const {
			includeScore = false,
			threshold = 0.2,
			findAllMatches = true,
			shouldSort = true,
		} = props.fuseOptions || {}

		const fuse = new Fuse(props.options, {
			...props.fuseOptions,
			keys: props.fuseKeys,
			includeScore,
			threshold,
			findAllMatches,
			shouldSort,
		})

		return query.value.length ?
				fuse.search(query.value).map((result) => result.item)
			:	props.options
	})

	const inputChange = useDebounceFn(
		(
			$event: Event & {
				target: HTMLInputElement
			}
		) => {
			props.onInputChange($event.target.value)
			query.value = $event.target.value
		},
		props.debounceDelay ?? 1
	)

	const inputFocus = () => {
		updateDropDirection()
		if (props.preventScrollOnFocus) {
			// Prevenir scroll usando scrollIntoView con opciones específicas
			if (inputElement.value?.el) {
				inputElement.value.el.scrollIntoView({
					behavior: 'auto',
					block: 'nearest',
					inline: 'nearest',
				})
			}
		}
		props.onFocus()
	}

	const inputBlur = () => {
		if (query.value != '' && props.resetQueryAfterSelect)
			setTimeout(() => (query.value = ''), 250)

		props.onBlur()
		showOptions.value = false
	}

	const canAdd = computed(() => {
		if (isArray(props.modelValue) && props.multiple && props.limit) {
			return props.modelValue.length < (props.limit ?? 0)
		}
		return true
	})

	const touchInput = () => {
		if (props.preventAutoFocus) return

		inputElement.value?.el.blur()
		setTimeout(() => {
			inputElement.value?.el.focus()
		}, 10)
	}

	watch(
		() => props.modelValue,
		(newModelValue) => {
			value.value = newModelValue
		}
	)

	watch(value, (newValue) => {
		if (!isArray(newValue) || (isArray(newValue) && canAdd)) {
			props.onModelChange ?
				props.onModelChange(newValue)
			:	emit('update:modelValue', newValue)
		}

		query.value = ''
		inputElement.value.el.value = null

		if (props.hideOptionsAfterSelect) {
			showOptions.value = false
		} else {
			touchInput()
		}

		props.afterModelChange && props.afterModelChange(newValue)

		if (newValue == undefined && !props.preventAutoFocus) {
			setTimeout(() => {
				inputElement.value.el.focus()
			}, 10)
		}
	})

	watch(query, (value) => {
		if (value == '') {
			props.onInputChange('')
			showOptions.value = false
		} else {
			updateDropDirection()
			showOptions.value = true
		}
	})

	// Listener para recalcular posición del panel si hace scroll/resize mientras está abierto
	const handleScrollResize = () => {
		if (showOptions.value) {
			updateDropDirection()
		}
	}

	watch(showOptions, (isOpen) => {
		if (isOpen) {
			// Agregar listeners cuando abre
			window.addEventListener('scroll', handleScrollResize, true)
			window.addEventListener('resize', handleScrollResize)
			// También escuchar scroll del contenedor padre más cercano
			const scrollParent = getScrollableParent(wrapperElement.value)
			if (scrollParent) {
				scrollParent.addEventListener('scroll', handleScrollResize)
			}
		} else {
			// Remover listeners cuando cierra
			window.removeEventListener('scroll', handleScrollResize, true)
			window.removeEventListener('resize', handleScrollResize)
			const scrollParent = getScrollableParent(wrapperElement.value)
			if (scrollParent) {
				scrollParent.removeEventListener('scroll', handleScrollResize)
			}
		}
	})
</script>

<template>
	<div>
		<Combobox
			ref="compoboxElement"
			as="div"
			:disabled="disabled"
			v-model="value"
			:multiple="multiple"
			:default-value="defaultValue as T"
			:by="(a: T, z: T) => (a && z ? a.id == z.id : false)"
			:class="['flex flex-col gap-1']"
			v-slot="{ open }"
			@update:model-value="(value) => emit('update:modelValue', value)"
		>
			<ComboboxLabel
				v-if="label"
				class="text-base font-medium text-gray-900 flex gap-2 items-center"
			>
				<p>
					{{ label }}
					<span
						v-if="isRequired"
						class="text-red-500"
						>*</span
					>
				</p>

				<LoadingSpin
					v-if="loadingOptions"
					class="!h-4 !w-4"
					dark
				/>
			</ComboboxLabel>

			<slot
				v-else
				name="label"
				:loading="loadingOptions"
				:required="isRequired"
			></slot>

			<div
				class="relative"
				ref="wrapperElement"
			>
				<div
					class="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm text-black"
				>
					<div :class="['w-full relative', error && !open && 'mb-[0.75rem]']">
						<ComboboxInput
							v-show="showInput"
							v-tooltip="inputTooltip"
							:model-value="query"
							ref="inputElement"
							:display-value="
								(item) => {
									return item ? displayValue(item as T) : ''
								}
							"
							:placeholder="placeholder"
							:aria-invalid="error ? 'true' : undefined"
							:class="[
								'py-[0.625rem] px-[0.545rem]  bg-white rounded-md w-full border-2 text-sm text-black',
								disabled &&
									(disabledClass ?? '!bg-gray-100 cursor-not-allowed'),
								preventScrollOnFocus && 'prevent-scroll-focus-input',
							]"
							:style="
								preventScrollOnFocus ?
									{ scrollMargin: '0', scrollPadding: '0' }
								:	{}
							"
							@focus="inputFocus"
							@blur="inputBlur"
							@change="inputChange"
							autocomplete="off"
						/>

						<div v-if="!showInput">
							<figure
								class="w-full h-[44px] rounded-md ring-2 ring-inset bg-white py-[0.438rem] px-[1.625rem] capitalize text-gray-900 shadow-sm sm:text-sm sm:leading-6 flex gap-2 justify-between items-center ring-gray-300"
								v-tooltip="inputOverlapTooltip"
							>
								<slot name="input-overlap"></slot>
							</figure>
						</div>

						<ComboboxButton
							v-show="showInput && showButton"
							class="absolute inset-y-0 right-0 flex items-center pr-2"
							@click.prevent="
								() => {
									updateDropDirection()
									showOptions = !showOptions
								}
							"
						>
							<ChevronUpDownIcon
								class="h-5 w-5 text-gray-400"
								aria-hidden="true"
							/>
						</ComboboxButton>
					</div>

					<span
						v-if="error && !open"
						class="!text-sm text-red-500 mt-6"
						>{{ error }}</span
					>
				</div>

				<teleport to="body">
					<TransitionRoot
						:show="showOptionsByButton ? showOptions : undefined"
						leave="transition ease-in duration-100"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
						@before-enter="beforeEnter"
						@after-enter="afterEnter"
						@after-leave="afterLeave"
						@before-leave="beforeEnter"
					>
						<ComboboxOptions
							v-if="canAdd"
							:static="showOptionsByButton"
							:class="[
								'fixed z-[9999] max-h-44 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
							]"
							:style="{
								top: `${panelPosition.top}px`,
								left: `${panelPosition.left}px`,
								width: `${panelPosition.width}px`,
							}"
						>
						<slot name="customOption" />

						<div
							v-if="!query.length && !filteredOptions.length"
							class="relative cursor-default select-none py-2 px-4 text-gray-700"
						>
							Escriba para buscar...
						</div>

						<div
							v-else-if="filteredOptions.length === 0 && query.length > 0"
							class="relative cursor-default select-none py-2 px-4 text-gray-700"
						>
							Sin resultados.
						</div>

						<div
							v-else-if="loadingOptions"
							class="relative cursor-default select-none py-2 px-4 text-gray-700"
						>
							Cargando...
						</div>

						<template v-else>
							<ComboboxOption
								v-for="(option, index) in filteredOptions"
								as="template"
								:key="index"
								:value="option"
								v-slot="{ selected, active }"
								@click="inputBlur"
							>
								<li
									:class="[
										'relative cursor-default select-none py-2 pr-4',
										active ?
											'bg-[#1F2937] text-white'
										:	'bg-white text-gray-900',
										selected ? 'pl-10' : 'pl-7',
									]"
								>
									<span
										class="block truncate"
										:class="{
											'font-medium': selected,
											'font-normal': !selected,
										}"
									>
										<slot
											:option="option as T"
											:active="active"
										/>
									</span>
									<span
										v-if="selected"
										class="absolute inset-y-0 left-0 flex items-center pl-3"
										:class="{ 'text-white': active, 'text-teal-600': !active }"
									>
										<CheckIcon
											class="h-5 w-5"
											aria-hidden="true"
										/>
									</span>
								</li>
							</ComboboxOption>
						</template>
					</ComboboxOptions>
				</TransitionRoot>
			</teleport>
			</div>
		</Combobox>
	</div>
</template>

<style scoped>
	.prevent-scroll-focus-input {
		scroll-margin: 0 !important;
		scroll-padding: 0 !important;
	}

	.prevent-scroll-focus-input:focus {
		scroll-margin: 0 !important;
		scroll-padding: 0 !important;
		scroll-behavior: auto !important;
	}
</style>
